import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { requestPos } from "./posApi";

// The visitor's website login: their BillZapp owner account (see routes/account.js in the
// POS backend). The login token is kept in this browser so it survives page loads.
const TOKEN_KEY = "sateriAccountToken";

function readToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    return null;
  }
}

function saveToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    // Storage blocked (private window): the login lasts until the page is closed.
  }
}

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const [token, setToken] = useState(readToken);
  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(() => Boolean(token)); // a saved login is being checked

  const signIn = useCallback(({ token: newToken, profile: newProfile }) => {
    saveToken(newToken);
    setToken(newToken);
    setProfile(newProfile);
  }, []);

  const signOut = useCallback(() => {
    saveToken(null);
    setToken(null);
    setProfile(null);
  }, []);

  // Calls the API as the logged-in owner. An expired login logs out, so the login form shows.
  const request = useCallback(async (path, options = {}) => {
    try {
      return await requestPos(path, { ...options, token });
    } catch (err) {
      if (err.status === 401 && token) signOut();
      throw err;
    }
  }, [token, signOut]);

  const refreshProfile = useCallback(async () => {
    const data = await request("/account/me");
    setProfile(data.profile);
    return data.profile;
  }, [request]);

  // Loads the profile for a login saved by an earlier visit.
  useEffect(() => {
    if (!token || profile) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    requestPos("/account/me", { token })
      .then((data) => !cancelled && setProfile(data.profile))
      .catch((err) => !cancelled && err.status === 401 && signOut())
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [token, profile, signOut]);

  const value = useMemo(() => ({
    status: checking ? "checking" : token && profile ? "signedIn" : "signedOut",
    profile,
    setProfile,
    signIn,
    signOut,
    request,
    refreshProfile,
  }), [checking, token, profile, signIn, signOut, request, refreshProfile]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const account = useContext(AccountContext);
  if (!account) throw new Error("useAccount must be used inside <AccountProvider>");
  return account;
}
